import { Entity } from '@backstage/catalog-model';
import { Dashboard } from '@backstage-community/plugin-grafana';

type Stage = {
  urls: string[];
  dbs: string[];
  aws_services: string[];
  vm: {
    language: string;
    version: string;
  };
  elbs: {
    cname: string;
    security_group: string;
  }[];
};

type Stages = {
  sandbox: Stage;
  production: Stage;
};

const telemetrySandboxURL = (names: string[]): string => {
  const params = names.map(name => `&var-service=${name}%2Fsandbox`).join('');
  return `https://vision.imedidata.net/d/f4d2c326-22a3-4063-8387-f111649cc438/service-overview?orgId=1${params}`;
};

const telemetryProductionURL = (names: string[]): string => {
  const params = names
    .map(name => `&var-service=${name}%2Fproduction`)
    .join('');
  return `https://vision.imedidata.com/d/b5cd0f20-8d38-405f-96f5-af1fda1a4502/service-overview?orgId=1${params}`;
};

const getDashboard = (
  title: string,
  url: string,
  stage: 'sandbox' | 'production',
  extraTags: string[] = [],
) => ({
  title: `${title} (${stage})`,
  url,
  folderTitle: 'Dashboards',
  folderUrl:
    stage === 'sandbox'
      ? 'https://vision.imedidata.net/dashboards'
      : 'https://vision.imedidata.com/dashboards',
  tags: ['Available', 'Service', ...extraTags],
});

export const getTeamReusableDashboards = (
  components: Entity[],
): Dashboard[] => {
  const dashboards: Dashboard[] = [];

  const telemetryNames = components
    .map(component => component.metadata.annotations?.['mdsol/telemetry-name'])
    .flatMap(f => f ?? []); // Filter potential null values

  if (telemetryNames.length > 0) {
    const urlSandbox = telemetrySandboxURL(telemetryNames);
    dashboards.push(
      getDashboard('Services Overview', urlSandbox, 'sandbox', [
        'MetricsFromTraces',
      ]),
    );

    const urlProduction = telemetryProductionURL(telemetryNames);
    dashboards.push(
      getDashboard('Services Overview', urlProduction, 'production', [
        'MetricsFromTraces',
      ]),
    );
  }

  return dashboards;
};

export const getReusableDashboards = (entity: Entity): Dashboard[] => {
  const dashboards: Dashboard[] = [];

  const stages: Stages = entity.metadata?.stages as Stages;

  const telemetryName = entity.metadata.annotations?.['mdsol/telemetry-name'];
  if (telemetryName) {
    const urlSandbox = telemetrySandboxURL([telemetryName]);
    dashboards.push(
      getDashboard('Service Overview', urlSandbox, 'sandbox', [
        'MetricsFromTraces',
      ]),
    );

    const urlProduction = telemetryProductionURL([telemetryName]);
    dashboards.push(
      getDashboard('Service Overview', urlProduction, 'production', [
        'MetricsFromTraces',
      ]),
    );
  }

  const cname = stages?.production?.elbs?.[0]?.cname;
  if (cname) {
    const rx = /https:\/\/(.*?)\..*/;
    const subdomain = RegExp(rx).exec(cname)?.[1];
    if (subdomain) {
      const albSandbox = `https://vision.imedidata.net/d/f646499f-f756-4a6e-a841-0bece1584fe7/alb-data?orgId=1&var-service=${subdomain}&var-stage=sandbox&var-operation=All&var-status_code=All`;
      dashboards.push(getDashboard('ALB Data', albSandbox, 'sandbox'));

      const albProduction = `https://vision.imedidata.com/d/f646499f-f756-4a6e-a841-0bece1584fe7/alb-data?orgId=1&var-service=${subdomain}&var-stage=production&var-operation=All&var-status_code=All`;
      dashboards.push(getDashboard('ALB Data', albProduction, 'production'));
    }
  }

  return dashboards;
};
